import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, MenuItem, Select, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EnhancedTable from '../common/EnhancedTable';
import { useStudentClassMapping } from '../../hooks/useStudentClassMapping';
import { useClasses } from '../../hooks/useClasses';
import { useAuth } from '../../contexts/AuthContext';
import { MARKSHEET_TYPE_OPTIONS } from '../enum/MarksheetTypeOptions';
import { useMarkSheets } from '../../hooks/useMarkSheets';
import MarksheetTemplate from '../templates/MarksheetTemplate';
import { useSchools } from '../../hooks/useSchools';
import { useTranslation } from 'react-i18next';

interface StudentHistoryModalProps {
  open: boolean;
  handleClose: () => void;
  studentId: number | null;
  studentName: string;
  rollNo: string;
}

const StudentHistoryModal: React.FC<StudentHistoryModalProps> = ({ open, handleClose, studentId, studentName, rollNo }) => {
  const { t } = useTranslation();
  const { useClassHistory } = useStudentClassMapping();
  const { data, isLoading, error } = useClassHistory(studentId ?? undefined);
  const { user } = useAuth();
  const { useSchoolById } = useSchools();
  const { data: school, isLoading: schoolLoading } = useSchoolById(
    typeof user?.schoolId === 'number' ? user.schoolId : undefined
  );
  const schoolId = user?.schoolId ?? undefined;
  const { classesQuery } = useClasses(schoolId);
  const { useMarksheetByTypeClassRoll } = useMarkSheets(false, true); // Disable marksheetsQuery to prevent automatic API call

  // State for marksheetType per row and marksheet data
  const [selectedMarksheetTypes, setSelectedMarksheetTypes] = useState<{ [rowIdx: number]: string }>({});
  const [viewingMarksheet, setViewingMarksheet] = useState<any>(null);
  const [loadingMarksheet, setLoadingMarksheet] = useState(false);
  const [marksheetError, setMarksheetError] = useState<string | null>(null);
  const [marksheetParams, setMarksheetParams] = useState<{ marksheetType: string, className: string, rollNo: string } | null>(null);

  // Call the hook at the top level, only when params are set
  const marksheetQuery = useMarksheetByTypeClassRoll(
    marksheetParams?.marksheetType || '',
    marksheetParams?.className || '',
    marksheetParams?.rollNo || '',
    !!marksheetParams
  );

  // When marksheetQuery.data changes, show the marksheet
  // React.useEffect(() => {
  //   if (marksheetQuery.data) {
  //     setViewingMarksheet(marksheetQuery.data);
  //     setLoadingMarksheet(false);
  //   }
  //   if (marksheetQuery.error || (marksheetQuery.isFetched && !marksheetQuery.data)) {
  //     setMarksheetError('Marksheet not found for this marksheet type or class.');
  //     setLoadingMarksheet(false);
  //   }
  // }, [marksheetQuery.data, marksheetQuery.error, marksheetQuery.isFetched]);

  React.useEffect(() => {
    if (!marksheetParams) return;
  
    if (marksheetQuery.isSuccess && marksheetQuery.data) {
      setViewingMarksheet(marksheetQuery.data);
      setLoadingMarksheet(false);
    } else if (marksheetQuery.isFetched && !marksheetQuery.isLoading && !marksheetQuery.data) {
      setMarksheetError(t('marksheet_not_found'));
      setLoadingMarksheet(false);
    } else if (marksheetQuery.isError) {
      setMarksheetError(t('error_fetching_marksheet'));
      setLoadingMarksheet(false);
    }
  }, [marksheetQuery.data, marksheetQuery.error, marksheetQuery.isFetched, marksheetQuery.isSuccess, marksheetParams]);
  

  const columns = [
    { id: 'session', label: t('session'), minWidth: 120, format: (val: any) => val?.name || '' },
    { id: 'className', label: t('class'), minWidth: 120 },
    { id: 'current', label: t('current_class'), minWidth: 80, format: (val: boolean) => val ? '✔️' : '' },
    { id: 'rollNo', label: t('roll_no'), minWidth: 80 },
  ];

  const getClassName = (classId: number) => {
    const cls = classesQuery.data?.find((cls: any) => cls.id === classId);
    return cls ? `${cls.name}${cls.section ? ' ' + cls.section : ''}` : '';
  };

  // Ensure data is an array and handle loading/error states
  const formattedData = React.useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    
    return data.map((row: any, idx: number) => ({
      session: row.session,
      className: getClassName(row.classId),
      current: row.current,
      rollNo: rollNo,
      _rowIdx: idx,
    }));
  }, [data, rollNo]);

  return (
    <Dialog open={open} 
    onClose={(event, reason) => {
      if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
        handleClose();
      }
    }} 
    maxWidth="md" 
    fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box flex={1} textAlign="center">
          <Typography variant="h6" fontWeight="bold">{t('student_name')} - {studentName}</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {viewingMarksheet ? (
          <Box position="relative">
            <IconButton
              onClick={() => setViewingMarksheet(null)}
              size="small"
              sx={{ position: 'absolute', top: 0, right: 0, zIndex: 2 }}
            >
              <CloseIcon />
            </IconButton>
            <MarksheetTemplate 
              {...viewingMarksheet} 
              schoolName={schoolLoading ? t('loading') : (school?.nameofSchool || viewingMarksheet.schoolName)} 
            />
          </Box>
        ) : (
          <>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{t('loading_student_history')}</Typography>
              </Box>
            ) : error ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <Typography color="error">
                  {t('error_loading_student_history')}: {error.message}
                </Typography>
              </Box>
            ) : formattedData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <Typography color="textSecondary">
                  {t('no_class_history_found')}
                </Typography>
              </Box>
            ) : (
              <>
                <EnhancedTable
                  columns={columns}
                  data={formattedData}
                  title={t('student_class_history')}
                  actions={(row: any) => {
                    const idx = row._rowIdx;
                    // Filter options based on class
                    const isBoardClass = row.className.startsWith('10th') || row.className.startsWith('12th');
                    let options: { value: string; label: string }[] = [];
                    if (isBoardClass) {
                      options = MARKSHEET_TYPE_OPTIONS.filter(opt => opt.value !== 'ANNUAL');
                    } else {
                      options = MARKSHEET_TYPE_OPTIONS.filter(opt => opt.value !== 'BOARD' && opt.value !== 'PRE_BOARD');
                    }
                    return (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Select
                          size="small"
                          value={selectedMarksheetTypes[idx] || options[0].value}
                          onChange={e => setSelectedMarksheetTypes(s => ({ ...s, [idx]: e.target.value }))}
                        >
                          {options.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                        <Typography
                          component="span"
                          sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', ml: 1 }}
                          onClick={() => {
                            const marksheetType = selectedMarksheetTypes[idx] || options[0].value;
                            if (!marksheetType || !row.className || !row.rollNo) return;
                            setLoadingMarksheet(true);
                            setMarksheetError(null);
                            setMarksheetParams({
                              marksheetType,
                              className: row.className,
                              rollNo: row.rollNo,
                            });
                          }}
                        >
                          {t('view')}
                        </Typography>
                      </Box>
                    );
                  }}
                />
                {loadingMarksheet && <CircularProgress sx={{ mt: 2 }} />}
                {marksheetError && <Typography color="error" sx={{ mt: 2 }}>{marksheetError}</Typography>}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StudentHistoryModal;